import { supabase } from '@/lib/supabase';

export const SPARKLOOP_PILOT_PARTNER_ID = 'de489aca-63de-4189-b440-cfa8b0c0b485';
export const isSparkLoopPilotPartner = (partnerId: string | null | undefined) =>
  partnerId === SPARKLOOP_PILOT_PARTNER_ID;
export type SparkLoopMode = 'test' | 'live';
export type InvitationPlacement = 'before_final_question' | 'after_first_question';
export interface QuizSparkLoopSettings {
  partnerId: string;
  quizId: string;
  publicationId: string;
  mode: SparkLoopMode;
  placement: InvitationPlacement;
  cooldownHours: 0 | 24 | 168;
}
export interface PartnerSparkLoopSettings extends Omit<QuizSparkLoopSettings, 'quizId'> {
  enabled: boolean;
  quizIds: string[];
}
function settingsRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Invalid newsletter settings');
  return value as Record<string, unknown>;
}
function parseSettings(value: unknown) {
  const row = settingsRecord(value);
  if (
    typeof row.partnerId !== 'string' ||
    !isSparkLoopPilotPartner(row.partnerId) ||
    typeof row.publicationId !== 'string' ||
    !/^pub_[A-Za-z0-9]+$/.test(row.publicationId) ||
    (row.mode !== 'test' && row.mode !== 'live') ||
    (row.placement !== 'before_final_question' && row.placement !== 'after_first_question') ||
    ![0, 24, 168].includes(Number(row.cooldownHours)) ||
    typeof row.cooldownHours !== 'number'
  )
    throw new Error('Invalid newsletter settings');
  return {
    partnerId: String(row.partnerId),
    publicationId: row.publicationId,
    mode: row.mode as SparkLoopMode,
    placement: row.placement as InvitationPlacement,
    cooldownHours: row.cooldownHours as 0 | 24 | 168,
  };
}
export async function getQuizSparkLoopSettings(
  quizId: string
): Promise<QuizSparkLoopSettings | null> {
  const { data, error } = await supabase.rpc('get_quiz_sparkloop_settings', { p_quiz_id: quizId });
  if (error) throw error;
  if (data === null) return null;
  const row = settingsRecord(data);
  if (row.quizId !== quizId) throw new Error('Invalid newsletter Quiz');
  return { ...parseSettings(data), quizId };
}
function parsePartnerSettings(value: unknown, partnerId: string): PartnerSparkLoopSettings {
  const row = settingsRecord(value);
  if (
    row.partnerId !== partnerId ||
    typeof row.enabled !== 'boolean' ||
    !Array.isArray(row.quizIds) ||
    row.quizIds.length > 100 ||
    !row.quizIds.every((id) => typeof id === 'string' && /^[0-9a-f-]{36}$/i.test(id))
  )
    throw new Error('Invalid newsletter settings');
  return { ...parseSettings(value), enabled: row.enabled, quizIds: row.quizIds as string[] };
}
export async function getPartnerSparkLoopSettings(
  partnerId: string
): Promise<PartnerSparkLoopSettings | null> {
  const { data, error } = await supabase.rpc('get_partner_sparkloop_settings', {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  return data === null ? null : parsePartnerSettings(data, partnerId);
}
export async function savePartnerSparkLoopSettings(
  settings: PartnerSparkLoopSettings
): Promise<PartnerSparkLoopSettings> {
  const { data, error } = await supabase.rpc('save_partner_sparkloop_settings', {
    p_partner_id: settings.partnerId,
    p_enabled: settings.enabled,
    p_mode: settings.mode,
    p_placement: settings.placement,
    p_cooldown_hours: settings.cooldownHours,
    p_quiz_ids: settings.quizIds,
  });
  if (error) throw error;
  return parsePartnerSettings(data, settings.partnerId);
}
